import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/Button';
import { useOrg } from '../OrgProvider';
import { canManageOrg } from '../org-roles';
import { fetchOrgMembers, addOrgMember, removeOrgMember } from '../api';
import type { OrgMember } from '../types';

export function MembersPanel() {
  const { getToken } = useAuth();
  const { activeOrg } = useOrg();
  const canManage = canManageOrg(activeOrg);
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member'>('member');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!activeOrg) return;
    setLoading(true);
    try {
      const token = await getToken();
      if (token) setMembers(await fetchOrgMembers(activeOrg.id, token));
    } finally {
      setLoading(false);
    }
  }, [activeOrg, getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!activeOrg || !email.trim()) return;
    setAdding(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) return;
      await addOrgMember(activeOrg.id, { email: email.trim(), role }, token);
      setEmail('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add member');
    } finally {
      setAdding(false);
    }
  }

  async function handleRemove(userId: string) {
    if (!activeOrg) return;
    const token = await getToken();
    if (!token) return;
    await removeOrgMember(activeOrg.id, userId, token);
    await load();
  }

  if (!activeOrg) {
    return <p className="text-sm text-dim">Select an organization first.</p>;
  }

  return (
    <div className="space-y-6">
      {/* Add member form — owners and admins only, as the server enforces. */}
      {canManage && (
        <form onSubmit={(e) => void handleAdd(e)} className="space-y-3">
          <h3 className="text-sm font-semibold text-ink">Add Member</h3>
          <div className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="member@example.com"
              className="lc-input flex-1 py-1.5"
            />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as 'admin' | 'member')}
              className="lc-input w-auto py-1.5"
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
            <Button type="submit" size="sm" disabled={adding || !email.trim()}>
              {adding ? 'Adding…' : 'Add'}
            </Button>
          </div>
          {error && <p className="text-xs text-del">{error}</p>}
        </form>
      )}

      {/* Members list */}
      {loading ? (
        <p className="text-sm text-dim">Loading members…</p>
      ) : members.length === 0 ? (
        <p className="text-sm text-dim">No members yet.</p>
      ) : (
        <div className="overflow-hidden border border-rule">
          <table className="min-w-full text-sm">
            <thead className="bg-bg">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-dim">User</th>
                <th className="px-4 py-2 text-left font-medium text-dim">Role</th>
                <th className="px-4 py-2 text-left font-medium text-dim">Joined</th>
                <th className="px-4 py-2 text-right font-medium text-dim">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-rule-soft">
              {members.map((m) => (
                <tr key={m.id}>
                  <td className="px-4 py-2 font-mono text-xs text-ink truncate max-w-[200px]">
                    {m.user_id}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                        m.role === 'owner'
                          ? 'bg-accent-soft text-accent'
                          : m.role === 'admin'
                            ? 'bg-warn/15 text-warn'
                            : 'bg-bg text-dim'
                      }`}
                    >
                      {m.role}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-dim">
                    {new Date(m.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {canManage && m.role !== 'owner' && (
                      <button
                        type="button"
                        onClick={() => void handleRemove(m.user_id)}
                        className="text-xs text-del hover:text-del/80"
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
